/**
 * Swing pivot detection.
 *
 * A high pivot is a bar whose HIGH exceeds the highs of the N bars on each side; a low pivot is a
 * bar whose LOW is below the lows of the N bars on each side. N defaults to 5: a bar must dominate
 * the five bars either side of it to count as a swing.
 */

/**
 * OHLC column layout, matching how App.js builds barData:
 *   ohlc: sortedData.map(item => [item.open, item.close, item.low, item.high])
 *
 * ECharts' candlestick order, NOT conventional open/high/low/close — low is index 2 and high is
 * index 3. Reading it as OHLC would take the CLOSE where the low belongs, which yields pivots that
 * look plausible and are quietly wrong.
 */
export const OHLC_LOW = 2;
export const OHLC_HIGH = 3;

/**
 * Bars required either side of a swing.
 *
 * Raised from 2 to 5. Two bars admits almost every minor wiggle, and both studies search for
 * spacings among the pivots they are given: the more pivots there are, the more spacings exist,
 * and the easier it is for an addition series or a Fibonacci ratio to find a match by coincidence.
 * Five bars yields far fewer, more significant swings, which makes a match mean more.
 */
export const DEFAULT_PIVOT_STRENGTH = 5;

export const PIVOT_HIGH = 'high';
export const PIVOT_LOW = 'low';

const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/**
 * Finds swing highs and lows.
 *
 * Comparison is STRICT on both sides, so a flat top of equal highs yields no pivot rather than
 * several adjacent ones. A run of identical bars is not a swing, and admitting it would seed the
 * series searches with clusters of pivots one bar apart that match almost any spacing.
 *
 * The first and last `strength` bars can never qualify — there are not enough bars beside them to
 * judge — so they are skipped rather than compared against a shorter window, which would make an
 * edge bar easier to promote than an interior one.
 *
 * @param {Array<number[]>} ohlc rows of [open, close, low, high]
 * @param {number} strength bars required on each side (default 5)
 * @returns {{highs:number[], lows:number[], all:Array<{index:number,type:string,price:number}>}}
 */
export function detectPivots(ohlc, strength = DEFAULT_PIVOT_STRENGTH) {
  const empty = { highs: [], lows: [], all: [] };
  if (!Array.isArray(ohlc) || ohlc.length === 0) return empty;
  const n = Math.max(1, Math.floor(strength) || 1);
  if (ohlc.length < n * 2 + 1) return empty;

  const highs = [];
  const lows = [];

  for (let i = n; i < ohlc.length - n; i++) {
    const bar = ohlc[i];
    if (!bar) continue;
    const high = num(bar[OHLC_HIGH]);
    const low = num(bar[OHLC_LOW]);

    if (high !== null) {
      let isHigh = true;
      for (let k = 1; k <= n && isHigh; k++) {
        const l = ohlc[i - k] && num(ohlc[i - k][OHLC_HIGH]);
        const r = ohlc[i + k] && num(ohlc[i + k][OHLC_HIGH]);
        if (l === null || r === null || high <= l || high <= r) isHigh = false;
      }
      if (isHigh) highs.push(i);
    }

    if (low !== null) {
      let isLow = true;
      for (let k = 1; k <= n && isLow; k++) {
        const l = ohlc[i - k] && num(ohlc[i - k][OHLC_LOW]);
        const r = ohlc[i + k] && num(ohlc[i + k][OHLC_LOW]);
        if (l === null || r === null || low >= l || low >= r) isLow = false;
      }
      if (isLow) lows.push(i);
    }
  }

  const all = [
    ...highs.map(i => ({ index: i, type: PIVOT_HIGH, price: ohlc[i][OHLC_HIGH] })),
    ...lows.map(i => ({ index: i, type: PIVOT_LOW, price: ohlc[i][OHLC_LOW] })),
  ].sort((a, b) => a.index - b.index);

  return { highs, lows, all };
}

/**
 * Bar distances of each pivot measured BACKWARDS from an anchor bar.
 *
 * Distance is `anchorIndex - pivotIndex`, so it grows leftwards from the anchor and pivots to the
 * RIGHT of it are dropped rather than given negative distances — a series counted back from bar 0
 * has nothing to say about bars after it.
 *
 * @returns {Array<{distance:number, index:number, type:string, price:number}>} nearest first
 */
export function pivotDistancesFrom(pivots, anchorIndex) {
  if (!Array.isArray(pivots) || typeof anchorIndex !== 'number') return [];
  return pivots
    .filter(p => p && typeof p.index === 'number' && p.index <= anchorIndex)
    .map(p => ({ ...p, distance: anchorIndex - p.index }))
    .sort((a, b) => a.distance - b.distance);
}

export default detectPivots;
